import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';
import {Link} from 'react-router-dom';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {statusColors} from '@/types/Transaction';
import {
  getBusinessTxChannel,
  getBusinessTxDisplayAmount,
} from '@/utils/businessTransactionFormatter';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import TransactionSummary from './TransactionSummary';

const BusinessTransactionTable = ({
  items,
}: {
  items: AfriexBusinessTransaction[];
}) => {
  // Rows an admin action changed since this page of items arrived. Dropped
  // when a new page or filter replaces `items`.
  const [updatedById, setUpdatedById] = useState<
    Record<string, AfriexBusinessTransaction>
  >({});
  const [seenItems, setSeenItems] = useState(items);
  if (seenItems !== items) {
    setSeenItems(items);
    setUpdatedById({});
  }

  return (
    <Table className="overflow-hidden">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th>Amount</Th>
          <Th>Asset</Th>
          <Th className="w-auto">Status</Th>
          <Th>Type</Th>
          <Th>Channel</Th>
          <Th>Date Initiated</Th>
          <Th>&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody data-cy="merchant-transactions-table-body">
        {items.map((item, index) => (
          <TRow
            key={item.id ?? index}
            // Keep the list's own `type` (formatAdminBusinessTransaction
            // prefixes admin-initiated ones); the fetched row is unprefixed.
            item={
              updatedById[item.id]
                ? {...item, ...updatedById[item.id], type: item.type}
                : item
            }
            index={index}
            onUpdated={(updated) =>
              setUpdatedById((prev) => ({...prev, [updated.id]: updated}))
            }
          />
        ))}
      </TBody>
    </Table>
  );
};

const TRow = ({
  item,
  index,
  onUpdated,
}: {
  item: AfriexBusinessTransaction;
  index: number;
  onUpdated: (transaction: AfriexBusinessTransaction) => void;
}) => {
  const transactionStatus = item.status;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // Payouts (WITHDRAW and SEND) move money out to a recipient, so transaction
  // monitoring needs the destination (fulfilled) currency, not the source.
  const {amount: displayAmount, asset: displayAsset} =
    getBusinessTxDisplayAmount(item);

  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  const redirectLink = `/merchants/transactions/${item.id}`;

  return (
    <>
      <Tr
        className={zebraRow}
        data-cy="merchant-transactions-row"
        data-transaction-id={item.id}
      >
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            data-cy="merchant-transactions-row-expand"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>

        <Td className={`font-medium`}>{displayAmount}</Td>

        <Td>{displayAsset}</Td>

        <Td>
          <Badge
            label={transactionStatus}
            colorScheme={statusColors[transactionStatus]}
          />
        </Td>

        <Td className={`capitalize`}>
          {capitalizeWords(item.type?.replace(/_/g, ' '))}
        </Td>

        <Td className={`capitalize`}>
          {capitalizeWords(
            (getBusinessTxChannel(item) || '').replace(/_/g, ' '),
          )}
        </Td>

        <Td>{formatDate.DDMonYYYY(item.createdAt?.toString() || '')}</Td>

        <Td>
          <Link
            to={redirectLink}
            data-cy="merchant-transactions-row-details-link"
          >
            <p className="flex items-center text-sm text-gray-700 dark:text-gray-300 hover:underline hover:text-cyan-600">
              View <ChevronRightIcon className="w-5 h-5" />
            </p>
          </Link>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={9} className="p-0 m-0 whitespace-nowrap">
            <div
              className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full"
              data-cy="merchant-transactions-row-summary"
              data-transaction-id={item.id}
            >
              <TransactionSummary
                data={item}
                isMerchant={true}
                onUpdated={onUpdated}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default BusinessTransactionTable;
